import { shallowRef, ref } from 'vue'
import { env } from '@/config/env'
import type { PictureSources } from '@/types/Media'
import { urlToPictureSources } from '@/services/media/imageUrlHelper'
import { requirePictureSources } from '@/services/media/imageRegistry'

export interface SiteAssets {
  logo: PictureSources
  pageBreaker: PictureSources
  pageBreakerHunde: PictureSources
  placeholder: PictureSources
  heroes: {
    home: PictureSources
    aboutUs: PictureSources
    members: PictureSources
  }
  icons: {
    schwertkampf: PictureSources
    handwerk: PictureSources
    feuershow: PictureSources
    termine: PictureSources
  }
}

interface SiteAssetsFirestoreDoc {
  logo: string
  pageBreaker: string
  pageBreakerHunde: string
  placeholder: string
  heroes: { home: string; aboutUs: string; members: string }
  icons: { schwertkampf: string; handwerk: string; feuershow: string; termine: string }
}

const siteAssets = shallowRef<SiteAssets | null>(null)
const loading = ref(false)
let loaded = false

async function ensureLoaded(): Promise<SiteAssets> {
  if (loaded && siteAssets.value) return siteAssets.value

  loading.value = true
  try {
    siteAssets.value = env.features.firebaseCms
      ? await loadFromFirestore()
      : loadFromLocalRegistry()
    loaded = true
    return siteAssets.value
  } finally {
    loading.value = false
  }
}

async function loadFromFirestore(): Promise<SiteAssets> {
  try {
    const { fetchDocument } = await import('@/services/firebase/firestoreClient')
    const doc = await fetchDocument<SiteAssetsFirestoreDoc>('config', 'siteAssets')
    return mapUrlsToAssets(doc)
  } catch (error) {
    if (env.features.devLogging) {
      // eslint-disable-next-line no-console
      console.warn('[useSiteAssets] Firestore load failed, falling back to local:', error)
    }
    return loadFromLocalRegistry()
  }
}

function mapUrlsToAssets(doc: SiteAssetsFirestoreDoc): SiteAssets {
  return {
    logo: urlToPictureSources(doc.logo),
    pageBreaker: urlToPictureSources(doc.pageBreaker),
    pageBreakerHunde: urlToPictureSources(doc.pageBreakerHunde),
    placeholder: urlToPictureSources(doc.placeholder),
    heroes: {
      home: urlToPictureSources(doc.heroes.home),
      aboutUs: urlToPictureSources(doc.heroes.aboutUs),
      members: urlToPictureSources(doc.heroes.members)
    },
    icons: {
      schwertkampf: urlToPictureSources(doc.icons.schwertkampf),
      handwerk: urlToPictureSources(doc.icons.handwerk),
      feuershow: urlToPictureSources(doc.icons.feuershow),
      termine: urlToPictureSources(doc.icons.termine)
    }
  }
}

function loadFromLocalRegistry(): SiteAssets {
  return {
    logo: requirePictureSources('logo'),
    pageBreaker: requirePictureSources('PageBreaker'),
    pageBreakerHunde: requirePictureSources('Mitglieder/Pagebreak_Hanuta'),
    placeholder: requirePictureSources('Platzhalter_Under_Construction'),
    heroes: {
      home: requirePictureSources('Home/Home_Hero_Image'),
      aboutUs: requirePictureSources('AboutUs/Ueber_Uns_Header_Image'),
      members: requirePictureSources('Mitglieder/Mitglieder_HeroImage')
    },
    icons: {
      schwertkampf: requirePictureSources('Icons/Schwertkampf'),
      handwerk: requirePictureSources('Icons/Handwerk'),
      feuershow: requirePictureSources('Icons/Feuershow'),
      termine: requirePictureSources('Icons/Termine')
    }
  }
}

export function useSiteAssets() {
  return { siteAssets, loading, ensureLoaded }
}

/** Reset for testing */
export function _resetSiteAssets(): void {
  siteAssets.value = null
  loading.value = false
  loaded = false
}
